import { useAppDispatch } from '../store/hooks/reduxHooks';
import { clearAuth } from '../store/slices/authSlice';

// ================= CONSTANTS =================
const INACTIVITY_TIMEOUT = 1 * 60 * 1000; // 30 min (use 1 * 60 * 1000 for testing)
const SESSION_HEARTBEAT_KEY = 'session_heartbeat';
const HEARTBEAT_INTERVAL = 3000; // 3 sec
const HEARTBEAT_EXPIRY = 5000;   // 5 sec (must be > interval)
const ABSOLUTE_SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour

// ================= HEARTBEAT =================
export const checkSessionHeartbeat = (): {
  exists: boolean;
  timestamp: number | null;
  isValid: boolean;
  expiryTime: number;
} => {
  const heartbeat = localStorage.getItem(SESSION_HEARTBEAT_KEY);
  if (!heartbeat) {
    return { exists: false, timestamp: null, isValid: false, expiryTime: HEARTBEAT_EXPIRY };
  }

  const timestamp = parseInt(heartbeat, 10);
  const isValid = Date.now() - timestamp <= HEARTBEAT_EXPIRY;

  return { exists: true, timestamp, isValid, expiryTime: HEARTBEAT_EXPIRY };
};

export const refreshHeartbeat = () => {
  localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
};

// ================= LOGOUT =================
export const clearAllStorage = () => {
  localStorage.clear();
  sessionStorage.clear();

  document.cookie.split(';').forEach(c => {
    document.cookie = c.replace(/^ +/, '')
      .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
  });

  if ('caches' in window) {
    caches.keys().then(names => names.forEach(name => caches.delete(name)));
  }
};

export const handleLogout = (dispatch: ReturnType<typeof useAppDispatch>) => {
  clearAllStorage();
  dispatch(clearAuth());
  window.location.href = '/login';
};

// ================= INACTIVITY LOGOUT =================
export const setupInactivityTimer = (dispatch: ReturnType<typeof useAppDispatch>) => {
  let inactivityTimer: NodeJS.Timeout;
  let absoluteTimer: NodeJS.Timeout;

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(() => {
      handleLogout(dispatch);
    }, INACTIVITY_TIMEOUT);
  };

  // 🔒 Absolute logout (never resets)
  absoluteTimer = setTimeout(() => {
    handleLogout(dispatch);
  }, ABSOLUTE_SESSION_TIMEOUT);

  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  events.forEach(event => window.addEventListener(event, resetInactivityTimer));

  resetInactivityTimer();

  return () => {
    clearTimeout(inactivityTimer);
    clearTimeout(absoluteTimer);
    events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
  };
};


// ================= TAB CLOSE / REFRESH HANDLING =================
export const setupBeforeUnloadHandler = (dispatch: ReturnType<typeof useAppDispatch>) => {
  // heartbeat keeps session alive
  const heartbeatInterval = setInterval(() => {
    localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
  }, HEARTBEAT_INTERVAL);

  // detect tab close (not refresh)
  let isReloading = false;

  window.addEventListener('beforeunload', () => {
    isReloading = true;
  });

  window.addEventListener('unload', () => {
    if (!isReloading) {
      // REAL tab close
      localStorage.removeItem(SESSION_HEARTBEAT_KEY);
    }
  });

  // logout if heartbeat expired (tab was closed)
  const lastHeartbeat = localStorage.getItem(SESSION_HEARTBEAT_KEY);
  if (lastHeartbeat && Date.now() - parseInt(lastHeartbeat, 10) > HEARTBEAT_EXPIRY) {
    handleLogout(dispatch);
  } else {
    localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
  }

  // multi-tab logout sync
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === SESSION_HEARTBEAT_KEY && e.newValue === null) {
      handleLogout(dispatch);
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    clearInterval(heartbeatInterval);
    window.removeEventListener('storage', handleStorageChange);
  };
};
